import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './services/api';
import { convertHeicToJpeg, isHeicFile } from './utils/convertHeic';

const PlanUpload = () => {
  const [planType, setPlanType] = useState('upload'); // 'upload', 'link', or 'none'
  const [units, setUnits] = useState('in'); // 'in' | 'cm'
  const [sheetOrientation, setSheetOrientation] = useState('portrait'); // 'portrait' | 'landscape'
  const [formData, setFormData] = useState({
    projectName: '',
    homeownerName: '',
    homeownerEmail: '',
    planImage: null,
    planLink: ''
  });
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    const validTypes = [
      'image/jpeg', 'image/png', 'image/jpg',
      'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence',
      'application/pdf'
    ];
    if (!validTypes.includes(file.type) && !isHeicFile(file)) {
      setError('Please upload a JPG, PNG, HEIC, or PDF file');
      return;
    }

    let workingFile = file;
    if (isHeicFile(workingFile)) {
      try {
        setError('');
        workingFile = await convertHeicToJpeg(workingFile);
      } catch (err) {
        setError(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
        return;
      }
    }

    if (workingFile.size > 4 * 1024 * 1024) {
      setError('File size must be less than 4MB');
      return;
    }

    setFormData(prev => ({ ...prev, planImage: workingFile }));
    setError('');

    if (workingFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(workingFile);
    } else {
      setPreview('pdf');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.projectName.trim()) {
      setError('Project name is required');
      return;
    }

    // Validate based on plan type
    if (planType === 'upload' && !formData.planImage) {
      setError('Please upload a plan image');
      return;
    }

    if (planType === 'link' && !formData.planLink.trim()) {
      setError('Please enter a plan link');
      return;
    }

    setUploading(true);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Create FormData for file upload (or regular form data)
      const uploadData = new FormData();
      
      // Only append file if uploading
      if (planType === 'upload' && formData.planImage) {
        uploadData.append('planImage', formData.planImage);
      }
      
      uploadData.append('projectName', formData.projectName);
      uploadData.append('homeownerName', formData.homeownerName);
      uploadData.append('homeownerEmail', formData.homeownerEmail);
      uploadData.append('designerId', user.id);
      uploadData.append('planType', planType);
      uploadData.append('units', units);
      uploadData.append('sheetOrientation', sheetOrientation);
      
      if (planType === 'link') {
        uploadData.append('planLink', formData.planLink.trim());
      }

      const response = await api.createProject(uploadData);
      
      // Navigate to the new project
      navigate(`/plan/${response.project.id}`);
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.error || 'Failed to create project';
      if (errorMessage.includes('413') || errorMessage.includes('Too Large') || errorMessage.includes('Content Too Large')) {
        setError('File is too large. Please compress your image to under 4MB or use a lower quality/resolution.');
      } else {
        setError(errorMessage);
      }
      console.error('Create project error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleCancel}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Create New Project</h1>
          <p className="mt-2 text-sm text-gray-600">
            Create a project with a plan upload, plan link, or no plan
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Sheet scale */}
            <div className="mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Units
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setUnits('in')}
                      className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                        units === 'in'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      Imperial (in)
                    </button>
                    <button
                      type="button"
                      onClick={() => setUnits('cm')}
                      className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                        units === 'cm'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      Metric (cm)
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Controls how symbol sizes are displayed/entered in this project.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sheet Orientation (Base Map)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setSheetOrientation('portrait')}
                      className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                        sheetOrientation === 'portrait'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                      title="24 in wide × 36 in high"
                    >
                      24×36 Portrait
                    </button>
                    <button
                      type="button"
                      onClick={() => setSheetOrientation('landscape')}
                      className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                        sheetOrientation === 'landscape'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                      title="36 in wide × 24 in high"
                    >
                      36×24 Landscape
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Default: 24×36 portrait. You can change this later in Project Settings.
                  </p>
                </div>
              </div>
            </div>

            {/* Plan Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Option
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setPlanType('upload');
                    setFormData(prev => ({ ...prev, planImage: null, planLink: '' }));
                    setPreview(null);
                  }}
                  className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                    planType === 'upload'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  Upload Plan
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPlanType('link');
                    setFormData(prev => ({ ...prev, planImage: null }));
                    setPreview(null);
                  }}
                  className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                    planType === 'link'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  Link to Plan
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPlanType('none');
                    setFormData(prev => ({ ...prev, planImage: null, planLink: '' }));
                    setPreview(null);
                  }}
                  className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                    planType === 'none'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  No Plan
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {planType === 'upload' && 'Upload a plan image or PDF file'}
                {planType === 'link' && 'Enter a URL link to your plan'}
                {planType === 'none' &&
                  'Create project without a plan — you can still collaborate, upload files, and build a Project Plant List per project. To bulk-fill your catalog across projects, use Dashboard → Master Plant List (CSV).'}
              </p>
            </div>

            {/* File Upload Area - Only show if planType is 'upload' */}
            {planType === 'upload' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Image/PDF *
                </label>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-lg p-8 text-center ${
                  dragActive ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
                }`}
              >
                {preview ? (
                  <div className="space-y-4">
                    {preview === 'pdf' ? (
                      <div className="flex items-center justify-center">
                        <svg className="h-24 w-24 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" />
                        </svg>
                      </div>
                    ) : (
                      <img
                        src={preview}
                        alt="Preview"
                        className="max-h-64 mx-auto rounded"
                      />
                    )}
                    <p className="text-sm text-gray-600">{formData.planImage?.name}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, planImage: null }));
                        setPreview(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <>
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="mt-4">
                      <label className="cursor-pointer">
                        <span className="text-green-600 hover:text-green-700 font-medium">
                          Upload a file
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/jpeg,image/png,image/jpg,image/heic,image/heif,.heic,.heif,application/pdf"
                          onChange={handleFileChange}
                        />
                      </label>
                      <span className="text-gray-600"> or drag and drop</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      JPG, PNG or PDF up to 10MB
                    </p>
                  </>
                )}
              </div>
            </div>
            )}

            {/* Plan Link - Only show if planType is 'link' */}
            {planType === 'link' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Link *
                </label>
                <input
                  type="url"
                  name="planLink"
                  value={formData.planLink}
                  onChange={handleInputChange}
                  placeholder="https://example.com/plan.pdf or https://drive.google.com/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required={planType === 'link'}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter a URL to your plan (PDF, image, or shared document link)
                </p>
              </div>
            )}

            {/* Project Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                placeholder="e.g., Smith Backyard Garden"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            {/* Homeowner Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Homeowner/Client Name
              </label>
              <input
                type="text"
                name="homeownerName"
                value={formData.homeownerName}
                onChange={handleInputChange}
                placeholder="e.g., John Smith"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Homeowner Email */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Homeowner/Client Email
              </label>
              <input
                type="email"
                name="homeownerEmail"
                value={formData.homeownerEmail}
                onChange={handleInputChange}
                placeholder="john@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional - for sharing the plan with your client
              </p>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={uploading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  planType === 'upload' ? 'Upload Plan' : 'Create Project'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlanUpload;
